import * as React from 'react';

import InputMask from 'react-input-mask';
import { NumericFormat } from 'react-number-format';
import { twMerge } from 'tailwind-merge';

import { FloatingLabel } from '../floating-label';
import { IInputProps, TCommonInputProps } from './types';

const CommonInput = React.forwardRef<HTMLInputElement, TCommonInputProps>(
  ({ size, placeholder, className, isCustomPlaceholder, ...rest }, ref) => {
    return (
      <input
        ref={ref}
        {...rest}
        className={twMerge(
          Boolean(placeholder) && !isCustomPlaceholder && 'self-end',
          'peer w-full flex-1 select-none appearance-none truncate border-none bg-transparent px-0 py-0 shadow-none outline-none ring-0 typography-16r file:border-0 file:bg-surface-00-transparent file:text-sm file:font-medium file:text-base-500',
          size !== 'sm' && 'placeholder:text-transparent',
          className,
        )}
        placeholder={placeholder}
      />
    );
  },
);
const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      id,
      className,
      error,
      isInvalid,
      onValueChange,
      onChange,
      placeholder,
      customPlaceholder,
      afterIcon,
      description,
      variant,
      size,
      mask,
      maskInputProps = {},
      isNumber,
      numberInputProps = { decimalScale: 2, allowNegative: false },
      beforeIcon,
      disabled,
      classNames = {},
      value,
      inputClassName,
      ...props
    },
    ref,
  ) => {
    const changeValueHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
      onValueChange?.(event.currentTarget.value);
      onChange?.(event);
    };

    return (
      <FloatingLabel
        htmlFor={id}
        placeholder={placeholder ?? ''}
        customPlaceholder={customPlaceholder}
        variant={variant}
        isInvalid={isInvalid || Boolean(error)}
        disabled={disabled}
        className={className ?? ''}
        description={description ?? ''}
        isInput
        size={size}
        classNames={{
          wrapper: classNames.wrapper,
          description: classNames.description,
          placeholder: classNames.placeholder,
        }}
      >
        {beforeIcon}
        {mask ? (
          <InputMask
            inputRef={ref}
            {...props}
            {...maskInputProps}
            className={twMerge(
              Boolean(placeholder) && !customPlaceholder && 'self-end',
              'peer flex-1 appearance-none border-none bg-transparent px-0 py-0 shadow-none outline-none ring-0 typography-16r file:border-0 file:bg-surface-00-transparent file:text-sm file:font-medium file:text-base-500',
              size !== 'sm' &&
                !customPlaceholder &&
                'placeholder:text-transparent',
              classNames.input,
            )}
            value={value}
            mask={mask}
            placeholder={placeholder}
            formatChars={
              maskInputProps.formatChars ?? {
                '#': '[0-9]',
                '?': '[0-9]',
              }
            }
            onChange={changeValueHandler}
            maskChar={maskInputProps.maskChar ?? '_'}
          />
        ) : isNumber ? (
          <NumericFormat
            {...props}
            {...numberInputProps}
            allowedDecimalSeparators={
              numberInputProps?.allowedDecimalSeparators ?? ['.', ',']
            }
            decimalScale={
              numberInputProps?.decimalScale === undefined
                ? 2
                : numberInputProps?.decimalScale
            }
            value={value}
            thousandSeparator={numberInputProps?.thousandSeparator ?? ' '}
            allowNegative={numberInputProps?.allowNegative ?? false}
            allowLeadingZeros={numberInputProps?.allowLeadingZeros ?? false}
            disabled={disabled}
            placeholder={placeholder || customPlaceholder}
            customInput={CommonInput}
            className={inputClassName}
            isCustomPlaceholder={Boolean(customPlaceholder)}
            onValueChange={({ value }) => {
              onValueChange?.(value);
            }}
          />
        ) : (
          <CommonInput
            ref={ref}
            {...props}
            value={value}
            disabled={disabled}
            className={inputClassName}
            onChange={changeValueHandler}
            size={size}
            isCustomPlaceholder={Boolean(customPlaceholder)}
            placeholder={placeholder || customPlaceholder}
          />
        )}
        {afterIcon}
      </FloatingLabel>
    );
  },
);

export { Input };
